'use client'

import React, { useEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/cn'
import styles from './liquid-metal-surface.module.css'

/**
 * The liquid-metal button's "clear" variant, reusable as a content-agnostic
 * card. A translucent tinted face sits over a metal layer — faithful to
 * design/stammbaum-six.html `.lmw[data-face="clear"]`. Not a button: put
 * whatever you like inside (icon + title + text).
 *
 * The metal layer is a STATIC brushed-metal gradient by default. When `active`,
 * the real `@paper-design/shaders` WebGL metal runs live underneath the face —
 * but each live shader is its own GPU context, and several at once over the
 * full-screen lava background crash the renderer. So callers keep at most ONE
 * surface `active` at a time (e.g. a row that spotlights one card, rotating).
 * Under prefers-reduced-motion the live shader is skipped (static gradient).
 */
interface LiquidMetalSurfaceProps {
  children: React.ReactNode
  interactive?: boolean
  /** run the live WebGL shader on this surface (keep to one at a time) */
  active?: boolean
  /** border radius in rem (default 1) */
  radius?: number
  /** shader inset of the translucent face, in px (default 3 → thin metal rim) */
  rim?: number
  className?: string
}

export function LiquidMetalSurface({
  children,
  interactive = false,
  active = false,
  radius = 1,
  rim = 3,
  className,
}: LiquidMetalSurfaceProps) {
  const shaderRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shaderMount = useRef<any>(null)
  const [shaderLoaded, setShaderLoaded] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const styleId = 'shader-canvas-style-exploded'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `.shader-container-exploded canvas { width:100% !important; height:100% !important; display:block !important; position:absolute !important; top:0 !important; left:0 !important; }`
      document.head.appendChild(style)
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    let isMounted = true
    const cleanup = () => {
      shaderMount.current?.destroy?.()
      shaderMount.current = null
      setShaderLoaded(false)
    }
    if (!active || prefersReducedMotion) {
      cleanup()
      return
    }
    ;(async () => {
      try {
        const { liquidMetalFragmentShader, ShaderMount } = await import('@paper-design/shaders')
        if (!shaderRef.current || !isMounted) return
        shaderMount.current?.destroy?.()
        shaderMount.current = new ShaderMount(
          shaderRef.current,
          liquidMetalFragmentShader,
          {
            u_repetition: 4,
            u_softness: 0.5,
            u_shiftRed: 0.3,
            u_shiftBlue: 0.3,
            u_distortion: 0,
            u_contour: 0,
            u_angle: 45,
            u_scale: 8,
            u_shape: 1,
            u_offsetX: 0.1,
            u_offsetY: -0.1,
          },
          undefined,
          0.4,
        )
        setShaderLoaded(true)
      } catch (error) {
        console.warn('[LiquidMetalSurface] WebGL shader unavailable, using fallback:', error)
      }
    })()
    return () => {
      isMounted = false
      cleanup()
    }
  }, [active, prefersReducedMotion])

  return (
    <div
      className={cn(
        styles.surface,
        interactive && styles.interactive,
        active && styles.active,
        className,
      )}
      data-appearance="clear"
      style={{
        ['--lms-radius' as string]: `${radius}rem`,
        ['--lms-rim' as string]: `${rim}px`,
      }}
    >
      <div className={styles.shaderLayer} aria-hidden="true">
        {active && (
          <div
            ref={shaderRef}
            className="shader-container-exploded"
            style={{
              position: 'absolute',
              inset: 0,
              opacity: shaderLoaded ? 1 : 0,
              transition: 'opacity 0.6s ease',
            }}
          />
        )}
      </div>
      <div className={styles.face} aria-hidden="true" />
      <div className={styles.content}>{children}</div>
    </div>
  )
}
